import type { JobAttribute } from '../../../../__generated__/types.gqlgenerated';

import { filterAttributesByType } from './attributeFiltering';
import { sortAttributesByType } from './attributeSorting';
import { useLogFeatureDisplay } from '../../../../homepage/logging/DisplayLog';
import { Tag, Text , Box, type BoxRef } from '../../../../mads';
import { Check } from '../../../../mads/icons';

import { colors } from '@indeed/ifl-tokens/react-native';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet } from 'react-native';

export const MAX_LINES = 2;
export const GAP_HEIGHT = 4;

export type AttributeType = 'jobTypes' | 'shiftAndSchedule' | 'benefits';

export function AttributeComponent({
    attributes,
    type,
    jobKey,
    isHighlightPositive = true,
    onTagHeightChanged,
}: {
    attributes: JobAttribute[];
    type: AttributeType;
    jobKey: string;
    isHighlightPositive?: boolean;
    onTagHeightChanged?: (height: number) => void;
}) {
    const logFeatureDisplay = useLogFeatureDisplay();
    const sortedAndFilteredAttributes = useMemo(() => {
        const filtered = filterAttributesByType(attributes, type);
        return sortAttributesByType(filtered, type);
    }, [attributes, type]);

    if (sortedAndFilteredAttributes.length === 0) return null;

    if (type === 'benefits') {
        logFeatureDisplay({
            jobKey: jobKey,
            field: 'benefits',
            value: sortedAndFilteredAttributes.map((attr) => attr.key),
        });
        return sortedAndFilteredAttributes.map((attr, idx) => (
            <TagContainer key={idx} onTagHeightChanged={onTagHeightChanged}>
                <Tag sx={styles.tag}>{attr.label}</Tag>
            </TagContainer>
        ));
    }

    const representativeAttribute =
        sortedAndFilteredAttributes.find((attribute) => attribute.sentiment === 'POSITIVE') ||
        sortedAndFilteredAttributes[0];
    const isPositive = representativeAttribute.sentiment === 'POSITIVE';
    const color =
        isPositive && isHighlightPositive
            ? colors.backgroundStatusSuccess
            : colors.backgroundStatusGeneric;

    const label = representativeAttribute.label;

    if (type === 'jobTypes') {
        logFeatureDisplay({
            jobKey: jobKey,
            field: 'jobType',
            value: representativeAttribute.key,
        });
        logFeatureDisplay({
            jobKey: jobKey,
            field: 'jobTypeYN',
            value: true,
        });
    } else if (type === 'shiftAndSchedule') {
        logFeatureDisplay({
            jobKey: jobKey,
            field: 'shift',
            value: representativeAttribute.key,
        });
        logFeatureDisplay({
            jobKey: jobKey,
            field: 'shiftYN',
            value: true,
        });
    }

    return (
        <TagContainer onTagHeightChanged={onTagHeightChanged}>
            <Tag
                sx={styles.tag}
                status={isPositive && isHighlightPositive ? 'success' : 'neutral'}
                contentAfter={
                    <MoreItemLabel attributesLength={sortedAndFilteredAttributes.length} color={color} />
                }
                icon={isPositive ? <Check size="xs" aria-hidden={true} /> : undefined}
            >
                {label}
            </Tag>
        </TagContainer>
    );
}

function MoreItemLabel({ attributesLength, color }: { attributesLength: number; color: string }) {
    if (attributesLength <= 1) return null;
    // No gettext needed: "+" and Arabic numerals are universal across locales
    return <Text level={1} sx={{ color: color }}>{` + ${attributesLength - 1}`}</Text>;
}

export function getMaxHeight(tagHeight: number): number {
    return (tagHeight + GAP_HEIGHT) * MAX_LINES;
}

const TagContainer = ({
    children,
    onTagHeightChanged,
}: {
    children: React.ReactNode;
    onTagHeightChanged?: (height: number) => void;
}) => {
    const ref = useRef<BoxRef | null>(null);
    const [display, setDisplay] = useState<'none' | 'flex'>('flex');
  
    // Render the tag if it fits within the first 2 lines of the container
    useLayoutEffect(() => {
      if (ref.current) {
        ref.current?.measure((x, y, width, height) => {
            const tagHeight = height;
            const tagY = y;

            if(tagHeight > 0 && tagY < getMaxHeight(tagHeight)) {
                setDisplay('flex');
            } else {
                setDisplay('none');
            }

            if (onTagHeightChanged) {
                onTagHeightChanged(tagHeight);
            }
          });
      }
    }, [onTagHeightChanged]);


    return (
        <Box
            ref={ref}
            sx={[styles.tagContainer, { display }]}
            {...Platform.select({
                ios: { accessible: true },
                android: { screenReaderFocusable: true },
            })}
        >
            {children}
        </Box>
    );
};

const styles = StyleSheet.create({
    tagContainer: {
        marginLeft: 1,
        marginTop: 1,
        flexShrink: 1,
        maxWidth: '100%',
    },
    tag: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        minHeight: 24,
    },
});
